import {
  CountType,
  FunnelsCountType,
  InsightsCountType,
  RetentionCountType,
} from 'common/query-builder/query-entry-section/types';
import {AggregationGroupLabel} from 'common/query-builder/types/shared-types';
import {
  ALL_MATH_TYPES,
  MATH_TYPE,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {TableToggleableMetrics, TrendChartType} from 'common/report/funnels/models/types';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';

import {BehaviorType} from '../constants';

const HELP_DOCS_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#insights-data-functions-operators-and-calculations)`;

/** List of selections derived from selected count type */
export enum SubSelectionTypes {
  Property = `property`,
  AggregationPerUser = `aggregationPerUser`,
  Aggregation = `aggregation`,
}

export type GroupLabelParams = {[key in AggregationGroupLabel]: string};

export type SelectorLabel = string | ((params: GroupLabelParams) => string);

/**
 * Definition of count type in count menu
 * i.e. Uniques, Total Events, Property per Unique, etc.
 */
export type CountTypeSelector = {
  countType: CountType;

  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;

  /** 1st level aggregation definitions for the given count type */
  aggregations?: AggregationSelector[];
  /** Specifies if the aggregation acts on a property */
  forProperty?: boolean;

  /** List of selections required to be able to commit */
  requiredSubSelections?: SubSelectionTypes[];
  /** Specifies if the given count type can be committed */
  hasRequiredSelections: (param: {[key in SubSelectionTypes]}) => boolean;
};

/**
 * Definition of aggregation that acts on count type
 * i.e. Sum, Average, Median, etc.
 */
export type AggregationSelector = {
  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;

  /**
   * 2nd level aggregation definitions for the 1st pass aggregation
   * Defines a list of aggregations associated with the given aggregation
   * e.g. Median (2nd level aggregation) Sum (1st pass aggregation) of Price per Unique
   */
  subAggregations?: AggregationSelector[];
  /**
   * Defines aggregations to be displayed in a nested menu
   * e.g. P25, P75, P90 for Percentile
   */
  subSections?: AggregationSelector[];
};

/**
 * Template variables for output copies for the selected count type
 * @example: aggregationOperator: total|average|median
 * @example: aggregationOperatorPerUser: total|average|median
 * @example: propertyName: Price|Revenue
 */
export enum CountSelections {
  AggregationOperator = `aggregationOperator`,
  AggregationOperatorPerUser = `aggregationOperatorPerUser`,
  PropertyName = `propertyName`,
}

export type CountOutputLabelParams = {[key in CountSelections | AggregationGroupLabel]: string};

export type CountOutputLabel = string | ((params: CountOutputLabelParams) => string);

export const OperatorToString = {
  [ALL_MATH_TYPES.TOTAL]: `Sum`,
  [ALL_MATH_TYPES.AVERAGE]: `Average`,
  [ALL_MATH_TYPES.MEDIAN]: `Median`,
  [ALL_MATH_TYPES.MIN]: `Minimum`,
  [ALL_MATH_TYPES.MAX]: `Maximum`,
  [ALL_MATH_TYPES.P25]: `P25`,
  [ALL_MATH_TYPES.P75]: `P75`,
  [ALL_MATH_TYPES.P90]: `P90`,
  [ALL_MATH_TYPES.P99]: `P99`,
  [ALL_MATH_TYPES.UNIQUE_VALUES]: `Distinct Count`,
  [ALL_MATH_TYPES.HISTOGRAM]: `Distribution`,
  [ALL_MATH_TYPES.UNIQUE]: `Default`,
};

export const XAU_OPTIONS = {
  [XAU_MATH_TYPES.DAU]: {
    label: ({pluralGroupTitle}) => `Daily Active ${pluralGroupTitle}`,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 24 hours${HELP_DOCS_LINK}`,
  },
  [XAU_MATH_TYPES.WAU]: {
    label: ({pluralGroupTitle}) => `Weekly Active ${pluralGroupTitle}`,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 7 days${HELP_DOCS_LINK}`,
  },
  [XAU_MATH_TYPES.MAU]: {
    label: ({pluralGroupTitle}) => `Monthly Active ${pluralGroupTitle}`,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 30 days${HELP_DOCS_LINK}`,
  },
};

export const MATH_OPERATORS_WITH_OF_IN_PROPERTY_LABEL = [
  ALL_MATH_TYPES.TOTAL,
  ALL_MATH_TYPES.UNIQUE_VALUES,
  ALL_MATH_TYPES.P25,
  ALL_MATH_TYPES.P75,
  ALL_MATH_TYPES.P90,
  ALL_MATH_TYPES.P99,
];

export const InsightsCountOutputLabelMappings = new Map<InsightsCountType, CountOutputLabel>([
  [InsightsCountType.UniqueUsers, ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`],
  [InsightsCountType.TotalEvents, InsightsCountType.TotalEvents],
  [InsightsCountType.TotalSessions, InsightsCountType.TotalSessions],
  [
    InsightsCountType.FrequencyPerUser,
    ({aggregationOperator, groupTitle}: CountOutputLabelParams) => {
      return aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
        ? `Frequency ${OperatorToString[aggregationOperator]} per ${groupTitle}`
        : `${OperatorToString[aggregationOperator]} Frequency per ${groupTitle}`;
    },
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, propertyName}: CountOutputLabelParams) => {
      const includeOf = MATH_OPERATORS_WITH_OF_IN_PROPERTY_LABEL.includes(
        aggregationOperator as NUMERIC_MATH_TYPES | PER_USER_AGGREGATIONS,
      );
      return includeOf
        ? `${OperatorToString[aggregationOperator]} of ${propertyName}`
        : `${OperatorToString[aggregationOperator]} ${propertyName}`;
    },
  ],
  [
    InsightsCountType.PropertyValuePerUser,
    ({aggregationOperator, aggregationOperatorPerUser, propertyName, groupTitle}: CountOutputLabelParams) => {
      const includeOf = MATH_OPERATORS_WITH_OF_IN_PROPERTY_LABEL.includes(
        aggregationOperatorPerUser as NUMERIC_MATH_TYPES | PER_USER_AGGREGATIONS,
      );
      const propertyLabel = includeOf
        ? `${OperatorToString[aggregationOperatorPerUser]} of ${propertyName} per ${groupTitle}`
        : `${OperatorToString[aggregationOperatorPerUser]} ${propertyName} per ${groupTitle}`;
      return `${OperatorToString[aggregationOperator]} (${propertyLabel})`;
    },
  ],
]);

export const RetentionCountOutputLabelMappings = new Map<RetentionCountType, CountOutputLabel>([
  [RetentionCountType.Unique, RetentionCountType.Unique],
  [RetentionCountType.RetentionRate, RetentionCountType.RetentionRate],
]);

export const FunnelsCountOutputLabelMappings = new Map<FunnelsCountType, CountOutputLabel>([
  [FunnelsCountType.Unique, `Uniques`],
  [FunnelsCountType.General, `Total Conversions`],
  [FunnelsCountType.Session, `Sessions`],
  [FunnelsCountType.ConversionRate, `Conversion Rate`],
  [FunnelsCountType.TimeToConvert, `Time to Convert`],
]);

export const FunnelsCountToTrendMapping = new Map<FunnelsCountType, TrendChartType>([
  [FunnelsCountType.Unique, TrendChartType.TopOfFunnel],
  [FunnelsCountType.General, TrendChartType.TopOfFunnel],
  [FunnelsCountType.Session, TrendChartType.TopOfFunnel],
  [FunnelsCountType.ConversionRate, TrendChartType.Conversion],
  [FunnelsCountType.TimeToConvert, TrendChartType.TimeToConvert],
]);

export const FunnelsCountToFrequencyMapping = new Map<FunnelsCountType, FrequencyViewType>([
  [FunnelsCountType.Unique, FrequencyViewType.Conversion],
  [FunnelsCountType.General, FrequencyViewType.Conversion],
  [FunnelsCountType.Session, FrequencyViewType.Conversion],
  [FunnelsCountType.ConversionRate, FrequencyViewType.ConversionRate],
  [FunnelsCountType.TimeToConvert, FrequencyViewType.ConversionRate], // TODO should this be disabled instead?
]);

export const FunnelsCountToMetricColumnMapping = new Map<FunnelsCountType, TableToggleableMetrics>([
  [FunnelsCountType.Unique, TableToggleableMetrics.Count],
  [FunnelsCountType.General, TableToggleableMetrics.Count],
  [FunnelsCountType.Session, TableToggleableMetrics.Count],
  [FunnelsCountType.ConversionRate, TableToggleableMetrics.ConvPrevStep],
  [FunnelsCountType.TimeToConvert, TableToggleableMetrics.TimePrevStep],
]);

export const CountOutputLabelMappings = new Map<BehaviorType, Map<CountType, CountOutputLabel>>([
  [BehaviorType.Simple, InsightsCountOutputLabelMappings],
  [BehaviorType.Funnel, FunnelsCountOutputLabelMappings],
  [BehaviorType.Retention, RetentionCountOutputLabelMappings],
]);

export const InsightsCountSubMenuHeader = {
  [InsightsCountType.UniqueUsers]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({pluralGroup}) =>
        `Select how the number of ${pluralGroup} who perform an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.FrequencyPerUser]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({group}) =>
        `Select how the number of events per ${group} is calculated across all instances of that event. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValues]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: `Select how property values from each instance of an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValuePerUser]: {
    [SubSelectionTypes.AggregationPerUser]: {
      label: ({groupTitle}) => `Aggregate per ${groupTitle} by`,
      description: `Select how property values from each instance of an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({group}) =>
        `Select how the number of events per ${group} is calculated across all instances of that event. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
};

const FrequencyPerUniqueAggregations: AggregationSelector[] = [
  {
    label: `Distribution`,
    operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) =>
      `A distribution of the number of times an event is performed per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Average across all users of the event count per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Median of the event count per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        shortLabel: `P25`,
        operator: NUMERIC_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 25th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        shortLabel: `P75`,
        operator: NUMERIC_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 75th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        shortLabel: `P90`,
        operator: NUMERIC_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 90th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Minimum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Maximum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
  },
];

const PropertyAggregations: AggregationSelector[] = [
  {
    label: `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `Counts the total of a numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
  },
  {
    label: `Distinct Count`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: `The number of distinct property values across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: `P25`,
        description: `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: `P75`,
        description: `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: `P90`,
        description: `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `99th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P99,
        shortLabel: `P99`,
        description: `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
];

const PropertyPerUniqueAggregations: AggregationSelector[] = [
  {
    label: `Sum`,
    perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: ({group}) => `The total of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}) => `Average of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}) => `Median of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Percentile`,
        subSections: [
          {
            label: `25th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P25,
            perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
            shortLabel: `P25`,
            description: ({group}) => `The 25th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `75th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P75,
            perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
            shortLabel: `P75`,
            description: ({group}) => `The 75th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `90th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P90,
            perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
            shortLabel: `P90`,
            description: ({group}) => `The 90th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
          },
        ] as AggregationSelector[],
      },
      {
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}) => `Minimum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
        description: ({group}) => `Maximum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Average`,
    perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: ({group}) => `Average of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}) => `Sum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}) => `Average of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}) => `Median of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Percentile`,
        subSections: [
          {
            label: `25th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P25,
            perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
            shortLabel: `P25`,
            description: ({group}) =>
              `The 25th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `75th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P75,
            perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
            shortLabel: `P75`,
            description: ({group}) =>
              `The 75th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `90th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P90,
            perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
            shortLabel: `P90`,
            description: ({group}) => `The 90th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
          },
        ] as AggregationSelector[],
      },
      {
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}) => `Minimum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
        description: ({group}) => `Maximum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Distinct Count`,
    perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: ({group}) => `The number of distinct property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}) => `Average of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}) => `Sum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({pluralGroup}) =>
          `A distribution of ${pluralGroup} by the distinct property value counts${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}) => `The total of the distinct property values counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Percentile`,
        subSections: [
          {
            label: `25th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P25,
            perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
            shortLabel: `P25`,
            description: ({group}) =>
              `The 25th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `75th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P75,
            perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
            shortLabel: `P75`,
            description: ({group}) =>
              `The 75th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `90th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P90,
            perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
            shortLabel: `P90`,
            description: ({group}) =>
              `The 90th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
          },
        ] as AggregationSelector[],
      },
      {
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}) => `Minimum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
        description: ({group}) => `Maximum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
    description: ({group}) => `Minimum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}) => `Sum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}) => `Average of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({pluralGroup}) =>
          `A distribution of ${pluralGroup} by the minimum of property values${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}) => `Median of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Percentile`,
        subSections: [
          {
            label: `25th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P25,
            perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
            shortLabel: `P25`,
            description: ({group}) =>
              `The 25th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `75th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P75,
            perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
            shortLabel: `P75`,
            description: ({group}) =>
              `The 75th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `90th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P90,
            perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
            shortLabel: `P90`,
            description: ({group}) =>
              `The 90th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
          },
        ] as AggregationSelector[],
      },
      {
        label: `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
        description: ({group}) => `Maximum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Maximum`,
    perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
    description: ({group}) => `Maximum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}) => `Sum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}) => `Average of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({pluralGroup}) =>
          `A distribution of ${pluralGroup} by the maximum of property values${HELP_DOCS_LINK}`,
      },
      {
        label: `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}) => `Median of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `Percentile`,
        subSections: [
          {
            label: `25th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P25,
            perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
            shortLabel: `P25`,
            description: ({group}) =>
              `The 25th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `75th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P75,
            perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
            shortLabel: `P75`,
            description: ({group}) =>
              `The 75th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
          },
          {
            label: `90th Percentile`,
            operator: PRIMITIVE_MATH_TYPES.P90,
            perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
            shortLabel: `P90`,
            description: ({group}) =>
              `The 90th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
          },
        ] as AggregationSelector[],
      },
      {
        label: `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
        description: ({group}) => `Minimum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
];

export const INSIGHTS_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: InsightsCountType.UniqueUsers,
    label: ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description: ({pluralGroup}) => `The number of ${pluralGroup} who performed an event${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: InsightsCountType.TotalEvents,
    label: InsightsCountType.TotalEvents,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The total number of events performed${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: InsightsCountType.TotalSessions,
    label: InsightsCountType.TotalSessions,
    operator: PRIMITIVE_MATH_TYPES.SESSIONS,
    description: `The total number of sessions that contain the selected event${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: InsightsCountType.FrequencyPerUser,
    label: ({groupTitle}) => `Frequency per ${groupTitle}`,
    description: ({group}) => `The number of events performed per ${group}${HELP_DOCS_LINK}`,
    aggregations: FrequencyPerUniqueAggregations,
    requiredSubSelections: [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
  },
  {
    countType: InsightsCountType.PropertyValues,
    label: InsightsCountType.PropertyValues,
    description: `Aggregate property value across all instances of an event${HELP_DOCS_LINK}`,
    aggregations: PropertyAggregations,
    forProperty: true,
    requiredSubSelections: [SubSelectionTypes.Property, SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({property, aggregation}) => !!property && !!aggregation,
  },
  {
    countType: InsightsCountType.PropertyValuePerUser,
    label: ({groupTitle}) => `Aggregate Property per ${groupTitle}`,
    description: ({group}) =>
      `Aggregate property values across all instances of an event per ${group}${HELP_DOCS_LINK}`,
    aggregations: PropertyPerUniqueAggregations,
    forProperty: true,
    requiredSubSelections: [
      SubSelectionTypes.Property,
      SubSelectionTypes.AggregationPerUser,
      SubSelectionTypes.Aggregation,
    ],
    hasRequiredSelections: ({property, aggregationPerUser, aggregation}) =>
      !!property && !!aggregationPerUser && !!aggregation,
  },
];

export const RETENTION_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: RetentionCountType.RetentionRate,
    label: `Retention Rate`,
    description: `Count the percentage rate for users who were retained within window Learn more${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: RetentionCountType.Unique,
    label: ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`,
    description: ({pluralGroupTitle}) => `Count ${pluralGroupTitle} who were retained within window${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
];

export const FUNNELS_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: FunnelsCountType.ConversionRate,
    label: `Conversion Rate`,
    description: `Count multiple entries per user. User may re-enter after converting or falling off${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    hasRequiredSelections: () => true,
  },
  {
    countType: FunnelsCountType.Unique,
    label: `Uniques`,
    description: `Count a single entry per user${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    hasRequiredSelections: () => true,
  },
  {
    countType: FunnelsCountType.General,
    label: `Total Conversion`,
    description: `Count multiple entries per user. User may re-enter after converting or dropping off${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: FunnelsCountType.Session,
    label: `Sessions`,
    description: `Count the number of sessions that convert${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
  },
  {
    countType: FunnelsCountType.TimeToConvert,
    label: `Time to Convert`,
    description: `Count multiple entries per user. User may re-enter after converting or falling off${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    hasRequiredSelections: () => true,
  },
];
